import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import type { Profile, Account } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

const editUserSchema = z.object({
  full_name: z.string().optional(),
  role: z.enum(['admin', 'user']),
  account_id: z.string().nullable(),
  status: z.enum(['active', 'suspended', 'deleted']),
}).refine((data) => {
  // Account is required for user role
  if (data.role === 'user' && !data.account_id) {
    return false
  }
  return true
}, {
  message: 'Account is required for users',
  path: ['account_id'],
})

type EditUserFormData = z.infer<typeof editUserSchema>

interface EditUserDialogProps {
  userId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditUserDialog({ userId, open, onOpenChange }: EditUserDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data: accounts } = useQuery({
    queryKey: ['accounts-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .eq('status', 'active')
        .order('name')
      if (error) throw error
      return data as Pick<Account, 'id' | 'name'>[]
    },
  })

  const { data: user, isLoading: isLoadingUser } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()
      if (error) throw error
      return data as Profile
    },
    enabled: !!userId && open,
  })

  const form = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      full_name: '',
      role: 'user',
      account_id: null,
      status: 'active',
    },
  })

  // Watch the role field to conditionally show/hide Account field
  const watchedRole = useWatch({ control: form.control, name: 'role' })

  // Auto-clear account_id when role changes to admin
  useEffect(() => {
    if (watchedRole === 'admin') {
      form.setValue('account_id', null)
    }
  }, [watchedRole, form])

  // Reset form when user data loads
  // Use individual setValue calls to ensure each field updates properly
  // This is more reliable than form.reset() with Select components
  useEffect(() => {
    if (user && open) {
      form.setValue('full_name', user.full_name || '')
      form.setValue('role', user.role as 'admin' | 'user')
      form.setValue('account_id', user.account_id)
      form.setValue('status', user.status as 'active' | 'suspended' | 'deleted')
    }
  }, [user, open, form])

  const editMutation = useMutation({
    mutationFn: async (data: EditUserFormData) => {
      if (!userId) throw new Error('User ID is required')
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: data.full_name || null,
          role: data.role,
          account_id: data.account_id,
          status: data.status,
        })
        .eq('id', userId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
      toast({
        title: 'User updated',
        description: 'The user has been successfully updated.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: EditUserFormData) => {
    editMutation.mutate(data)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information and permissions.
          </DialogDescription>
        </DialogHeader>

        {isLoadingUser ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={user?.email || ''} disabled />
                <p className="text-sm text-muted-foreground">
                  Email cannot be changed
                </p>
              </div>

              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="user">User</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Admins have full platform access. Users are scoped to their account.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchedRole === 'user' && (
              <FormField
                control={form.control}
                name="account_id"
                render={({ field }) => {
                  // Use the actual field value, falling back to 'none' for display
                  const selectValue = field.value || 'none'
                  return (
                  <FormItem>
                    <FormLabel>Account</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                      value={selectValue}
                      key={selectValue}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Select an account...</SelectItem>
                        {accounts?.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The account this user will be assigned to.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                  )
                }}
              />
            )}

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="suspended">Suspended</SelectItem>
                        <SelectItem value="deleted">Deleted</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Suspended and deleted users cannot access the platform
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={editMutation.isPending}>
                  {editMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Update User
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
